'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FOLLOWING_QUERY_KEY } from '@/modules/players/v3/constants/following';
import {
  FRIENDS_TAB_COUNTS_QUERY_KEY,
  SENT_REQUESTS_QUERY_KEY,
  SUGGESTED_FRIENDS_QUERY_KEY,
} from '@/modules/players/v3/constants/friends';
import { RELATIONSHIP_QUERY_KEY } from '@/modules/players/v3/hooks/use-get-relationship';
import { sendFriendRequest } from '@/utils/actions/players/friends/sendFriendRequest';

const ALREADY_CONNECTED = 409;

export interface UseSendFriendRequestParams {
  uuid: string;
  friendUuid: string;
  intent?: 'friend' | 'follow';
}

export const useSendFriendRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ uuid, friendUuid, intent }: UseSendFriendRequestParams) => {
      const res = await sendFriendRequest({ uuid, friendUuid, intent });
      const alreadyConnected = res.statusCode === ALREADY_CONNECTED;
      if (res.error && !alreadyConnected) {
        throw new Error(`Failed to send friend request: ${res.error}`);
      }
      return alreadyConnected;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUGGESTED_FRIENDS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SENT_REQUESTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [FOLLOWING_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [FRIENDS_TAB_COUNTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [RELATIONSHIP_QUERY_KEY] });
    },
  });
};
